export const NOTIFICATION_CATEGORY = Object.freeze({
  DIRECT_MESSAGE: 'directMessage',
  REPLY: 'reply',
  FOLLOW: 'follow',
  LIKE: 'like',
  REPOST: 'repost',
  MILESTONE: 'milestone',
  REENGAGEMENT: 'reengagement',
});

export const NOTIFICATION_CATEGORY_LIST = Object.values(NOTIFICATION_CATEGORY);

export const DEFAULT_ADMIN_NOTIFICATION_SETTINGS = Object.freeze({
  webPushEnabled: true,
  mobilePushEnabled: true,
  suppressWhenUserOnline: false,
  testAccountUserId: null,
  permissionPrompt: {
    enabled: true,
    repeatEveryDays: 1,
  },
  categories: {
    [NOTIFICATION_CATEGORY.DIRECT_MESSAGE]: true,
    [NOTIFICATION_CATEGORY.REPLY]: true,
    [NOTIFICATION_CATEGORY.FOLLOW]: true,
    [NOTIFICATION_CATEGORY.LIKE]: true,
    [NOTIFICATION_CATEGORY.REPOST]: true,
    [NOTIFICATION_CATEGORY.MILESTONE]: true,
    [NOTIFICATION_CATEGORY.REENGAGEMENT]: false,
  },
  rateLimits: {
    [NOTIFICATION_CATEGORY.DIRECT_MESSAGE]: { maxPerHour: 0 },
    [NOTIFICATION_CATEGORY.REPLY]: { maxPerHour: 10 },
    [NOTIFICATION_CATEGORY.FOLLOW]: { maxPerHour: 10 },
    [NOTIFICATION_CATEGORY.LIKE]: { maxPerHour: 3 },
    [NOTIFICATION_CATEGORY.REPOST]: { maxPerHour: 3 },
    [NOTIFICATION_CATEGORY.MILESTONE]: { maxPerHour: 0 },
    [NOTIFICATION_CATEGORY.REENGAGEMENT]: { maxPerHour: 1 },
  },
  email: {
    enabled: true,
    enviarQuandoTodosDispositivosOffline: true,
    limiteGlobalPorHora: 100,
    limiteGlobalPorDia: 500,
    atrasoPadraoMinutos: 60,
    intervaloMinimoMesmoTipoMinutos: 180,
    usarResumoParaCategoriasNaoUrgentes: true,
    categorias: {
      [NOTIFICATION_CATEGORY.DIRECT_MESSAGE]: {
        enabled: true,
        maxPerHour: 0,
        atrasoMinutos: 10,
        usarResumo: false,
      },
      [NOTIFICATION_CATEGORY.REPLY]: {
        enabled: true,
        maxPerHour: 6,
        atrasoMinutos: 15,
        usarResumo: false,
      },
      [NOTIFICATION_CATEGORY.FOLLOW]: {
        enabled: true,
        maxPerHour: 6,
        atrasoMinutos: 180,
        usarResumo: true,
      },
      [NOTIFICATION_CATEGORY.LIKE]: {
        enabled: true,
        maxPerHour: 4,
        atrasoMinutos: 360,
        usarResumo: true,
      },
      [NOTIFICATION_CATEGORY.REPOST]: {
        enabled: true,
        maxPerHour: 4,
        atrasoMinutos: 360,
        usarResumo: true,
      },
      [NOTIFICATION_CATEGORY.MILESTONE]: {
        enabled: true,
        maxPerHour: 0,
        atrasoMinutos: 5,
        usarResumo: false,
      },
      [NOTIFICATION_CATEGORY.REENGAGEMENT]: {
        enabled: false,
        maxPerHour: 1,
        atrasoMinutos: 720,
        usarResumo: true,
      },
    },
  },
  milestones: {
    enabled: true,
    followerCountMilestones: [
      {
        followerCount: 10,
        enabled: true,
        title: 'Parabens, {name}!',
        body: 'Voce chegou a {count} seguidores.',
      },
      {
        followerCount: 100,
        enabled: true,
        title: '100 seguidores',
        body: 'Voce chegou a {count} seguidores. Continue assim.',
      },
      {
        followerCount: 1000,
        enabled: true,
        title: 'Mil seguidores',
        body: 'Voce chegou a {count} seguidores na Liga.',
      },
      {
        followerCount: 5000,
        enabled: true,
        title: '5 mil seguidores',
        body: 'Voce chegou a {count} seguidores.',
      },
      {
        followerCount: 10000,
        enabled: true,
        title: '10 mil seguidores',
        body: 'Voce chegou a {count} seguidores.',
      },
      {
        followerCount: 100000,
        enabled: true,
        title: '100 mil seguidores',
        body: 'Voce chegou a {count} seguidores.',
      },
      {
        followerCount: 1000000,
        enabled: true,
        title: '1 milhao de seguidores',
        body: 'Voce chegou a {count} seguidores.',
      },
    ],
  },
});

export const DEFAULT_USER_NOTIFICATION_PREFERENCES = Object.freeze({
  browser: {
    enabled: true,
    promptDismissedAt: null,
    disabledAt: null,
    categories: {
      [NOTIFICATION_CATEGORY.DIRECT_MESSAGE]: true,
      [NOTIFICATION_CATEGORY.REPLY]: true,
      [NOTIFICATION_CATEGORY.FOLLOW]: true,
      [NOTIFICATION_CATEGORY.LIKE]: true,
      [NOTIFICATION_CATEGORY.REPOST]: true,
      [NOTIFICATION_CATEGORY.MILESTONE]: true,
      [NOTIFICATION_CATEGORY.REENGAGEMENT]: false,
    },
  },
  email: {
    enabled: true,
    categories: {
      [NOTIFICATION_CATEGORY.DIRECT_MESSAGE]: true,
      [NOTIFICATION_CATEGORY.REPLY]: true,
      [NOTIFICATION_CATEGORY.FOLLOW]: true,
      [NOTIFICATION_CATEGORY.LIKE]: true,
      [NOTIFICATION_CATEGORY.REPOST]: true,
      [NOTIFICATION_CATEGORY.MILESTONE]: true,
      [NOTIFICATION_CATEGORY.REENGAGEMENT]: false,
    },
  },
});

const normalizeCategoryFlags = (source, fallback) =>
  NOTIFICATION_CATEGORY_LIST.reduce((acc, category) => {
    acc[category] =
      typeof source?.[category] === 'boolean'
        ? source[category]
        : Boolean(fallback?.[category]);
    return acc;
  }, {});

const normalizeRateLimits = (source, fallback) =>
  NOTIFICATION_CATEGORY_LIST.reduce((acc, category) => {
    const nextValue = source?.[category]?.maxPerHour;
    const fallbackValue = fallback?.[category]?.maxPerHour || 0;
    acc[category] = {
      maxPerHour:
        Number.isInteger(nextValue) && nextValue >= 0
          ? nextValue
          : fallbackValue,
    };
    return acc;
  }, {});

const normalizeDelayMinutes = (value, fallbackValue) => {
  if (Number.isInteger(value) && value >= 0) {
    return value;
  }
  return fallbackValue;
};

const normalizeEmailCategories = (source, fallback) =>
  NOTIFICATION_CATEGORY_LIST.reduce((acc, category) => {
    const fallbackItem = fallback?.[category] || {};
    const nextItem = source?.[category] || {};
    const fallbackMaxPerHour =
      Number.isInteger(fallbackItem.maxPerHour) && fallbackItem.maxPerHour >= 0
        ? fallbackItem.maxPerHour
        : 0;
    const fallbackAtrasoMinutos =
      Number.isInteger(fallbackItem.atrasoMinutos) &&
      fallbackItem.atrasoMinutos >= 0
        ? fallbackItem.atrasoMinutos
        : 0;

    acc[category] = {
      enabled:
        typeof nextItem.enabled === 'boolean'
          ? nextItem.enabled
          : Boolean(fallbackItem.enabled),
      maxPerHour:
        Number.isInteger(nextItem.maxPerHour) && nextItem.maxPerHour >= 0
          ? nextItem.maxPerHour
          : fallbackMaxPerHour,
      atrasoMinutos: normalizeDelayMinutes(
        nextItem.atrasoMinutos,
        fallbackAtrasoMinutos
      ),
      usarResumo:
        typeof nextItem.usarResumo === 'boolean'
          ? nextItem.usarResumo
          : Boolean(fallbackItem.usarResumo),
    };
    return acc;
  }, {});

const normalizePermissionPrompt = (source, fallback) => {
  const fallbackDays =
    Number.isInteger(fallback?.repeatEveryDays) && fallback.repeatEveryDays >= 1
      ? fallback.repeatEveryDays
      : 1;

  return {
    enabled:
      typeof source?.enabled === 'boolean'
        ? source.enabled
        : Boolean(fallback?.enabled),
    repeatEveryDays:
      Number.isInteger(source?.repeatEveryDays) && source.repeatEveryDays >= 1
        ? source.repeatEveryDays
        : fallbackDays,
  };
};

const normalizeTemplateText = (value, fallback, maxLength = 240) => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return fallback;
  return text.slice(0, maxLength);
};

const mapMilestonesByCount = (items) =>
  (Array.isArray(items) ? items : []).reduce((acc, item) => {
    const followerCount = Number(item?.followerCount);
    if (Number.isInteger(followerCount) && followerCount > 0) {
      acc.set(followerCount, item);
    }
    return acc;
  }, new Map());

const normalizeFollowerCountMilestones = (source, fallback) => {
  const sourceByCount = mapMilestonesByCount(source?.followerCountMilestones);
  const fallbackItems = Array.isArray(fallback?.followerCountMilestones)
    ? fallback.followerCountMilestones
    : DEFAULT_ADMIN_NOTIFICATION_SETTINGS.milestones.followerCountMilestones;

  return fallbackItems.map((fallbackItem) => {
    const item = sourceByCount.get(fallbackItem.followerCount) || {};
    return {
      followerCount: fallbackItem.followerCount,
      enabled:
        typeof item.enabled === 'boolean'
          ? item.enabled
          : Boolean(fallbackItem.enabled),
      title: normalizeTemplateText(item.title, fallbackItem.title, 120),
      body: normalizeTemplateText(item.body, fallbackItem.body, 240),
    };
  });
};

const normalizeMilestones = (source, fallback) => {
  const base = fallback || DEFAULT_ADMIN_NOTIFICATION_SETTINGS.milestones;

  return {
    enabled:
      typeof source?.enabled === 'boolean'
        ? source.enabled
        : Boolean(base.enabled),
    followerCountMilestones: normalizeFollowerCountMilestones(source, base),
  };
};

const normalizeEmailSettings = (source, fallback) => {
  const base = fallback || DEFAULT_ADMIN_NOTIFICATION_SETTINGS.email;
  const fallbackLimiteGlobalPorHora =
    Number.isInteger(base?.limiteGlobalPorHora) && base.limiteGlobalPorHora >= 0
      ? base.limiteGlobalPorHora
      : 0;
  const fallbackLimiteGlobalPorDia =
    Number.isInteger(base?.limiteGlobalPorDia) && base.limiteGlobalPorDia >= 0
      ? base.limiteGlobalPorDia
      : 0;
  const fallbackAtrasoPadraoMinutos =
    Number.isInteger(base?.atrasoPadraoMinutos) && base.atrasoPadraoMinutos >= 0
      ? base.atrasoPadraoMinutos
      : 0;
  const fallbackIntervaloMinimoMesmoTipoMinutos =
    Number.isInteger(base?.intervaloMinimoMesmoTipoMinutos) &&
    base.intervaloMinimoMesmoTipoMinutos >= 0
      ? base.intervaloMinimoMesmoTipoMinutos
      : 0;

  return {
    enabled:
      typeof source?.enabled === 'boolean'
        ? source.enabled
        : Boolean(base.enabled),
    enviarQuandoTodosDispositivosOffline:
      typeof source?.enviarQuandoTodosDispositivosOffline === 'boolean'
        ? source.enviarQuandoTodosDispositivosOffline
        : Boolean(base.enviarQuandoTodosDispositivosOffline),
    limiteGlobalPorHora:
      Number.isInteger(source?.limiteGlobalPorHora) &&
      source.limiteGlobalPorHora >= 0
        ? source.limiteGlobalPorHora
        : fallbackLimiteGlobalPorHora,
    limiteGlobalPorDia:
      Number.isInteger(source?.limiteGlobalPorDia) &&
      source.limiteGlobalPorDia >= 0
        ? source.limiteGlobalPorDia
        : fallbackLimiteGlobalPorDia,
    atrasoPadraoMinutos: normalizeDelayMinutes(
      source?.atrasoPadraoMinutos,
      fallbackAtrasoPadraoMinutos
    ),
    intervaloMinimoMesmoTipoMinutos: normalizeDelayMinutes(
      source?.intervaloMinimoMesmoTipoMinutos,
      fallbackIntervaloMinimoMesmoTipoMinutos
    ),
    usarResumoParaCategoriasNaoUrgentes:
      typeof source?.usarResumoParaCategoriasNaoUrgentes === 'boolean'
        ? source.usarResumoParaCategoriasNaoUrgentes
        : Boolean(base.usarResumoParaCategoriasNaoUrgentes),
    categorias: normalizeEmailCategories(source?.categorias, base.categorias),
  };
};

export const normalizeAdminNotificationSettings = (value) => ({
  webPushEnabled:
    typeof value?.webPushEnabled === 'boolean'
      ? value.webPushEnabled
      : DEFAULT_ADMIN_NOTIFICATION_SETTINGS.webPushEnabled,
  mobilePushEnabled:
    typeof value?.mobilePushEnabled === 'boolean'
      ? value.mobilePushEnabled
      : DEFAULT_ADMIN_NOTIFICATION_SETTINGS.mobilePushEnabled,
  suppressWhenUserOnline:
    typeof value?.suppressWhenUserOnline === 'boolean'
      ? value.suppressWhenUserOnline
      : DEFAULT_ADMIN_NOTIFICATION_SETTINGS.suppressWhenUserOnline,
  testAccountUserId:
    Number.isInteger(value?.testAccountUserId) && value.testAccountUserId > 0
      ? value.testAccountUserId
      : null,
  permissionPrompt: normalizePermissionPrompt(
    value?.permissionPrompt,
    DEFAULT_ADMIN_NOTIFICATION_SETTINGS.permissionPrompt
  ),
  categories: normalizeCategoryFlags(
    value?.categories,
    DEFAULT_ADMIN_NOTIFICATION_SETTINGS.categories
  ),
  rateLimits: normalizeRateLimits(
    value?.rateLimits,
    DEFAULT_ADMIN_NOTIFICATION_SETTINGS.rateLimits
  ),
  email: normalizeEmailSettings(
    value?.email,
    DEFAULT_ADMIN_NOTIFICATION_SETTINGS.email
  ),
  milestones: normalizeMilestones(
    value?.milestones,
    DEFAULT_ADMIN_NOTIFICATION_SETTINGS.milestones
  ),
});

export const normalizeUserNotificationPreferences = (value) => {
  const disabledAt =
    typeof value?.browser?.disabledAt === 'string'
      ? value.browser.disabledAt
      : null;
  const rawEnabled = value?.browser?.enabled;
  let { enabled } = DEFAULT_USER_NOTIFICATION_PREFERENCES.browser;

  if (typeof rawEnabled === 'boolean') {
    enabled = rawEnabled;
    if (rawEnabled === false && !disabledAt) {
      enabled = true;
    }
  }

  return {
    browser: {
      enabled,
      promptDismissedAt:
        typeof value?.browser?.promptDismissedAt === 'string'
          ? value.browser.promptDismissedAt
          : null,
      disabledAt,
      categories: normalizeCategoryFlags(
        value?.browser?.categories,
        DEFAULT_USER_NOTIFICATION_PREFERENCES.browser.categories
      ),
    },
    email: {
      enabled:
        typeof value?.email?.enabled === 'boolean'
          ? value.email.enabled
          : DEFAULT_USER_NOTIFICATION_PREFERENCES.email.enabled,
      categories: normalizeCategoryFlags(
        value?.email?.categories,
        DEFAULT_USER_NOTIFICATION_PREFERENCES.email.categories
      ),
    },
  };
};
